import { DiscountUpdateEvent } from '@theme/events';

(() => {
  const root = window.Shopify?.routes?.root || '/';
  let cart = null;
  let requestId = 0;
  let expiryTimer;
  const normalize = (code) => String(code || '').trim().toUpperCase();
  const format = (cents, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency', currency,
  }).format(cents / 100);

  const render = () => {
    const applications = (cart?.cart_level_discount_applications || []).map((entry) => entry.discount_application || entry);
    // Never infer eligibility or percentages from marketing copy or an arbitrary URL.
    let discount = applications.find((entry) => entry.type === 'discount_code'
      && entry.target_type === 'line_item' && entry.target_selection === 'all'
      && entry.value_type === 'percentage' && Number(entry.value) > 0 && Number(entry.value) <= 100);
    const hasConfirmedDiscount = Boolean(discount);
    const now = Math.floor(Date.now() / 1000);
    let recheckDelay = 60000;
    if (discount) {
      const periods = [...document.querySelectorAll('[data-ua-promo-banner], [data-ua-offer-period]')]
        .filter((offer) => normalize(offer.dataset.code) === normalize(discount.title));
      if (periods.length && !periods.some((offer) => Number(offer.dataset.startsAt) <= now && now < Number(offer.dataset.endsAt))) discount = null;
      for (const offer of periods) {
        const start = Number(offer.dataset.startsAt);
        const end = Number(offer.dataset.endsAt);
        if (start > now) recheckDelay = Math.min(recheckDelay, (start - now) * 1000);
        if (end > now) recheckDelay = Math.min(recheckDelay, (end - now) * 1000);
      }
    }
    window.clearTimeout(expiryTimer);
    if (hasConfirmedDiscount) {
      // Revalidate open-page estimates, including offers expiring while the tab stays visible.
      expiryTimer = window.setTimeout(() => {
        cart = null;
        render();
        refresh();
      }, recheckDelay);
    }
    for (const node of document.querySelectorAll('[data-ua-promo-price]')) {
      const original = node.querySelector('[data-ua-price-original]');
      const preview = node.querySelector('[data-ua-price-preview]');
      if (!original || !preview) continue;
      const amount = Number(node.dataset.price);
      const discounted = discount ? Math.round(amount * (1 - Number(discount.value) / 100)) : amount;
      const valid = discount && Number.isFinite(amount) && amount > 0 && discounted < amount;
      original.hidden = Boolean(valid);
      preview.hidden = !valid;
      if (!valid) continue;
      node.querySelector('[data-ua-price-before]').textContent = format(amount, cart.currency);
      node.querySelector('[data-ua-price-after]').textContent = format(discounted, cart.currency);
      node.querySelector('[data-ua-price-code]').textContent = `${node.dataset.withCode} ${discount.title}`;
    }
  };

  const refresh = async () => {
    const id = ++requestId;
    try {
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error('Cart unavailable');
      const data = await response.json();
      if (id !== requestId) return;
      cart = data;
    } catch {
      if (id !== requestId) return;
      cart = null;
    }
    render();
  };

  const applyLandingCode = async () => {
    const url = new URL(window.location.href);
    const code = (url.searchParams.get('discount') || url.searchParams.get('promo') || '').trim();
    if (!code || code.length > 255) return refresh();
    try {
      const currentResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!currentResponse.ok) throw new Error('Cart unavailable');
      const current = await currentResponse.json();
      const codes = (current.discount_codes || []).map((entry) => entry.code);
      if (!codes.some((existing) => normalize(existing) === normalize(code))) codes.push(code);
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(',') }),
      });
      if (!response.ok) throw new Error('Discount unavailable');
      const updatedCart = await response.json();
      document.dispatchEvent(new DiscountUpdateEvent(updatedCart, 'urbaneo-promo-landing'));
    } catch {
      // Preserve normal storefront pricing on rejection/network errors; Shopify is authoritative.
    }
    return refresh();
  };

  document.addEventListener('cart:update', refresh);
  document.addEventListener('discount:update', refresh);
  document.addEventListener('variant:update', refresh);
  document.addEventListener('visibilitychange', refresh);
  const observer = new MutationObserver((mutations) => {
    if (mutations.some((mutation) => [...mutation.addedNodes].some((node) => node.nodeType === 1
      && (node.matches('[data-ua-promo-price]') || node.querySelector('[data-ua-promo-price]'))))) render();
  });
  observer.observe(document.body, { childList: true, subtree: true });
  applyLandingCode();
})();
